/**
 * Prose SEO long-form — sections enrichies pour chaque page service.
 * Provient du projet updiag-prototype. 1000-1500 mots de contenu unique par service.
 * Rendu dans ServicePage.tsx via <div dangerouslySetInnerHTML={{__html}} />.
 */

export interface DeepDiveSection {
  h2: string;
  html: string;
}

export const servicesDeepDive: Record<string, DeepDiveSection[]> = {
  dpe: [
    {
      h2: "Qu'est-ce que le DPE 3CL-DPE 2021 ?",
      html: `<p>Le Diagnostic de Performance Énergétique est un document officiel qui évalue la consommation d'énergie primaire d'un logement et ses émissions de gaz à effet de serre. Depuis sa refonte en juillet 2021, le DPE est <strong>opposable</strong> : le vendeur ou le bailleur engage sa responsabilité sur les informations transmises à l'acquéreur ou au locataire.</p>
<p>Concrètement, deux étiquettes sont attribuées au bien : l'étiquette énergie (A à G) et l'étiquette climat. Ces indicateurs permettent à l'acheteur de connaître la performance du bien et d'anticiper ses dépenses énergétiques annuelles. Le DPE est basé sur une méthode de calcul unique : la méthode 3CL-DPE, qui prend en compte la surface, l'orientation, l'isolation, les menuiseries, le chauffage et la production d'eau chaude.</p>`,
    },
    {
      h2: "Qui est concerné par le DPE obligatoire à Paris ?",
      html: `<p>Le DPE est obligatoire dans trois situations :</p>
<ul>
  <li>Toute vente de bien immobilier</li>
  <li>Toute mise en location d'un logement</li>
  <li>Construction neuve (DPE neuf)</li>
  <li>Copropriété de plus de 50 lots (DPE collectif)</li>
</ul>
<p>Certains biens sont exemptés : constructions provisoires (&lt; 2 ans), bâtiments agricoles non climatisés, monuments historiques, lieux de culte et biens occupés moins de 4 mois par an. À Paris, où le parc immobilier ancien (haussmannien et avant-guerre) est très présent, la majorité des biens sont concernés et doivent présenter un DPE à jour.</p>`,
    },
    {
      h2: "Comment se déroule un DPE à Paris ?",
      html: `<p>L'intervention de notre diagnostiqueur certifié dure entre <strong>45 minutes et 1h30</strong> selon la taille du logement. Elle se déroule en quatre étapes :</p>
<ol>
  <li><strong>Relevé des caractéristiques du bâti</strong> : année de construction, surface habitable, orientation, type de murs, type de toiture, menuiseries extérieures.</li>
  <li><strong>Inventaire des équipements</strong> : système de chauffage, production d'eau chaude sanitaire, ventilation, climatisation, dispositifs de régulation.</li>
  <li><strong>Prise de photos et mesures</strong> : le diagnostiqueur documente l'état réel des installations pour garantir l'opposabilité du rapport.</li>
  <li><strong>Calcul et restitution</strong> : le rapport est édité selon la méthode 3CL-DPE 2021 et transmis par email le jour même.</li>
</ol>`,
    },
    {
      h2: "Logement classé F ou G à Paris : vos obligations",
      html: `<p>Le calendrier d'interdiction des "passoires thermiques" impose des échéances progressives aux bailleurs franciliens :</p>
<ul>
  <li><strong>2025</strong> : logements classés G interdits à la nouvelle mise en location</li>
  <li><strong>2028</strong> : logements classés F interdits à la nouvelle location</li>
  <li><strong>2034</strong> : logements classés E également interdits</li>
</ul>
<p>Si votre logement est classé F ou G, plusieurs solutions existent : rénovation énergétique (isolation, changement de chaudière, VMC double flux) avec aide de <strong>MaPrimeRénov'</strong>, <strong>éco-PTZ</strong>, ou les <strong>CEE</strong> (Certificats d'Économies d'Énergie). Après travaux, un nouveau DPE permet de valoriser votre bien d'une à deux classes.</p>`,
    },
    {
      h2: "Prix d'un DPE à Paris en 2025",
      html: `<p>Nos tarifs pour un DPE à Paris et en Île-de-France débutent à <strong>130 € TTC</strong> pour un studio et vont jusqu'à <strong>180 € TTC</strong> pour une maison individuelle. Le prix dépend de plusieurs facteurs :</p>
<ul>
  <li>Surface habitable du bien</li>
  <li>Nombre de pièces à expertiser</li>
  <li>Type de système de chauffage (individuel, collectif, complexe)</li>
  <li>Accessibilité des équipements</li>
</ul>
<p>Nos tarifs sont tout compris : <strong>déplacement inclus</strong> dans toute l'Île-de-France, aucun supplément kilométrique, pas de frais cachés.</p>`,
    },
    {
      h2: "Erreurs courantes à éviter",
      html: `<ul>
  <li>Faire appel à un diagnostiqueur non certifié Cofrac (rapport nul et sans valeur)</li>
  <li>Oublier d'annexer le DPE à l'annonce immobilière (amende jusqu'à 3 000 €)</li>
  <li>Présenter un DPE caduc : les DPE réalisés avant 2018 ne sont plus valides</li>
  <li>Négliger les recommandations de travaux qui peuvent devenir obligatoires</li>
  <li>Confondre DPE vente et DPE collectif en copropriété</li>
</ul>`,
    },
  ],

  "pack-vente": [
    {
      h2: "Qu'est-ce que le Dossier de Diagnostic Technique (DDT) ?",
      html: `<p>Le DDT est un dossier réglementaire regroupant l'ensemble des diagnostics obligatoires lors d'une vente immobilière. Il doit être <strong>remis à l'acquéreur au plus tard lors de la signature du compromis de vente</strong> et annexé à l'acte authentique chez le notaire.</p>
<p>Le DDT n'est pas un document unique mais un ensemble de rapports individuels, chacun ayant sa propre durée de validité et ses règles spécifiques. Notre Pack Vente mutualise l'intervention pour réaliser tous ces diagnostics en un seul rendez-vous, ce qui représente un gain logistique et financier majeur pour le vendeur.</p>`,
    },
    {
      h2: "Quels diagnostics contient le Pack Vente ?",
      html: `<p>Le contenu du Pack Vente dépend de la nature du bien et de son année de construction :</p>
<ul>
  <li><strong>DPE</strong> : toujours obligatoire, valable 10 ans</li>
  <li><strong>Amiante</strong> : obligatoire si permis de construire avant le 1er juillet 1997</li>
  <li><strong>Plomb (CREP)</strong> : obligatoire pour les biens construits avant 1949</li>
  <li><strong>Électricité</strong> : si installation de plus de 15 ans, valide 3 ans</li>
  <li><strong>Gaz</strong> : si installation de plus de 15 ans, valide 3 ans</li>
  <li><strong>Termites</strong> : dans les zones déclarées par arrêté préfectoral</li>
  <li><strong>Loi Carrez</strong> : obligatoire en copropriété</li>
  <li><strong>ERP</strong> : État des Risques et Pollutions (gratuit, fourni par nos soins)</li>
</ul>`,
    },
    {
      h2: "Pourquoi choisir un pack plutôt que des diagnostics à l'unité ?",
      html: `<p>Commander un Pack Vente présente trois avantages majeurs par rapport aux diagnostics individuels :</p>
<ol>
  <li><strong>Économies significatives</strong> : jusqu'à 30 % de réduction versus les tarifs à l'unité grâce à la mutualisation du déplacement et du temps.</li>
  <li><strong>Gain de temps</strong> : un seul RDV de 1h30 à 3h au lieu de 4 à 6 interventions séparées réparties sur plusieurs jours.</li>
  <li><strong>Dossier unique cohérent</strong> : tous les rapports sont transmis en un seul envoi, prêts à être remis au notaire.</li>
</ol>`,
    },
    {
      h2: "Comment se déroule l'intervention ?",
      html: `<p>Notre équipe (diagnostiqueur DPE + partenaires spécialisés) intervient en coordination le même jour. L'ordre des diagnostics est optimisé pour limiter l'impact sur votre quotidien. Les prélèvements éventuels (amiante, plomb) sont réalisés de manière non destructive et discrète. Vous recevez l'ensemble des rapports sous <strong>48 à 72h</strong> après l'intervention.</p>`,
    },
  ],

  "pack-location": [
    {
      h2: "Les diagnostics obligatoires pour louer en 2025",
      html: `<p>Depuis la loi ALUR, les obligations des bailleurs se sont renforcées. Voici la liste exhaustive des diagnostics à annexer au bail :</p>
<ul>
  <li><strong>DPE</strong> : obligatoire, opposable, 10 ans de validité</li>
  <li><strong>ERP</strong> (État des Risques et Pollutions) : à actualiser à chaque bail</li>
  <li><strong>CREP plomb</strong> : biens avant 1949, valide 6 ans en location</li>
  <li><strong>Amiante parties privatives</strong> : biens avant 1997 (conseillé, non obligatoire)</li>
  <li><strong>Électricité / gaz</strong> : si installation &gt; 15 ans, valide 6 ans</li>
  <li><strong>Loi Boutin</strong> : mesurage surface habitable</li>
</ul>`,
    },
    {
      h2: "DPE opposable : vos responsabilités de bailleur",
      html: `<p>Depuis juillet 2021, le DPE engage votre responsabilité de bailleur. En cas d'erreur significative, le locataire peut demander des dommages-intérêts. Le DPE doit apparaître <strong>obligatoirement dans l'annonce de location</strong> avec les étiquettes énergie et climat visibles.</p>
<p>Depuis 2025, les logements classés G sont <strong>interdits à la nouvelle mise en location</strong>. Les classes F le seront en 2028, les E en 2034. Anticipez en planifiant votre rénovation énergétique.</p>`,
    },
    {
      h2: "Loi Boutin vs Loi Carrez : quelle différence ?",
      html: `<p>La <strong>Loi Boutin</strong> s'applique aux locations nues et vides. Elle calcule la <strong>surface habitable</strong> hors combles non aménagés, caves, garages, balcons et terrasses. Attention : elle diffère de la Loi Carrez (vente), qui inclut certaines surfaces exclues par Boutin. Notre équipe réalise les deux mesurages si nécessaire.</p>`,
    },
  ],

  amiante: [
    {
      h2: "Pourquoi un diagnostic amiante est-il obligatoire ?",
      html: `<p>L'amiante a été massivement utilisé dans la construction jusqu'à son interdiction en 1997. On le retrouve principalement dans les revêtements de sol (dalles vinyle-amiante), les cloisons, les flocages, les joints de menuiseries, les conduits de chauffage et les toitures. Son inhalation est à l'origine de cancers (mésothéliome, cancer du poumon) avec un temps de latence de 20 à 40 ans.</p>
<p>Le diagnostic amiante protège à la fois <strong>les occupants</strong> et les <strong>futurs intervenants</strong> (acquéreurs, entreprises de travaux). Il est exigé à la vente et fortement recommandé avant tous travaux de rénovation.</p>`,
    },
    {
      h2: "Comment se déroule le repérage amiante ?",
      html: `<p>Le diagnostiqueur réalise un repérage visuel des matériaux susceptibles de contenir de l'amiante dans les parties privatives du bien. En cas de doute ou de matériau suspect, il effectue des <strong>prélèvements</strong> qui seront analysés en laboratoire accrédité. Le rapport final liste les matériaux repérés, leur état de conservation et les préconisations de gestion.</p>
<p>À Paris, la densité d'immeubles anciens (haussmanniens, faubouriens, immeubles 1920-1970) rend ce diagnostic particulièrement important. Nous intervenons dans tous les arrondissements avec une expertise spécifique aux bâtis franciliens.</p>`,
    },
    {
      h2: "Que faire si de l'amiante est détecté ?",
      html: `<p>La détection d'amiante n'implique pas automatiquement des travaux de retrait. Selon l'état de conservation :</p>
<ul>
  <li><strong>Bon état</strong> : surveillance périodique tous les 3 ans</li>
  <li><strong>État intermédiaire</strong> : mesure d'empoussièrement</li>
  <li><strong>Mauvais état</strong> : travaux obligatoires (encapsulage, retrait)</li>
</ul>
<p>Dans tous les cas, un <strong>Dossier Amiante des Parties Privatives (DAPP)</strong> doit être tenu à jour et transmis aux occupants.</p>`,
    },
  ],

  plomb: [
    {
      h2: "Qu'est-ce que le CREP et pourquoi est-il crucial ?",
      html: `<p>Le plomb, présent dans les peintures anciennes (céruse), est un toxique puissant qui provoque le <strong>saturnisme</strong> : une intoxication chronique particulièrement dangereuse pour les jeunes enfants (atteintes neurologiques irréversibles) et les femmes enceintes. Depuis 1949, le plomb a été progressivement éliminé des peintures du bâtiment.</p>
<p>Le CREP recense les revêtements contenant du plomb et évalue leur état de dégradation. Il impose au vendeur ou bailleur des mesures de mise en sécurité lorsque le plomb est accessible et dégradé.</p>`,
    },
    {
      h2: "Comment se déroule un CREP à Paris ?",
      html: `<p>L'opérateur utilise un <strong>appareil à fluorescence X</strong> pour mesurer la teneur en plomb des revêtements, pièce par pièce. Chaque unité de diagnostic (mur, porte, fenêtre, plinthe) est analysée individuellement. Le rapport final comporte :</p>
<ul>
  <li>Un tableau par pièce des unités de diagnostic</li>
  <li>La concentration en plomb en mg/cm²</li>
  <li>L'état de conservation des revêtements plombifères</li>
  <li>Les préconisations et recommandations</li>
</ul>`,
    },
    {
      h2: "Validité et responsabilités",
      html: `<p>La durée de validité du CREP varie :</p>
<ul>
  <li><strong>Vente</strong> : 1 an si présence de plomb, illimité si absence</li>
  <li><strong>Location</strong> : 6 ans si présence de plomb, illimité si absence</li>
</ul>
<p>Si du plomb dégradé est détecté, le bailleur a l'obligation d'effectuer les travaux de mise en sécurité. En cas de saturnisme déclaré, le préfet peut imposer des travaux d'office.</p>`,
    },
  ],

  electricite: [
    {
      h2: "Pourquoi ce diagnostic est-il obligatoire ?",
      html: `<p>Environ <strong>30 % des incendies domestiques</strong> en France sont d'origine électrique, causés par des installations vieillissantes ou non conformes. Le diagnostic électrique a été rendu obligatoire en 2009 (vente) et 2017 (location) pour protéger les occupants et informer les acquéreurs.</p>`,
    },
    {
      h2: "Les 6 points de contrôle essentiels",
      html: `<p>Notre diagnostiqueur vérifie six points critiques selon la norme XP C 16-600 :</p>
<ol>
  <li><strong>Appareil général de coupure</strong> et sa commande accessible</li>
  <li><strong>Protection différentielle</strong> à l'origine de l'installation</li>
  <li><strong>Prise de terre</strong> et installation de mise à la terre</li>
  <li><strong>Liaison équipotentielle</strong> dans les locaux humides (salle de bain)</li>
  <li><strong>Protection contre les surintensités</strong> par disjoncteurs ou fusibles</li>
  <li><strong>Matériels vétustes ou inadaptés</strong></li>
</ol>`,
    },
    {
      h2: "Que faire en cas d'anomalies ?",
      html: `<p>Le diagnostic est <strong>informatif</strong> : vous n'êtes pas obligé de faire les travaux avant la vente. Mais des anomalies graves peuvent impacter la négociation. En location, le bailleur a une obligation de délivrance d'un logement décent, et l'installation électrique doit être sûre.</p>`,
    },
  ],

  gaz: [
    {
      h2: "Un diagnostic essentiel pour la sécurité",
      html: `<p>Le gaz est responsable de <strong>plusieurs dizaines d'accidents graves chaque année en France</strong>, dont des intoxications au monoxyde de carbone (CO) parfois mortelles. Ces accidents sont souvent liés à des installations vieillissantes, une ventilation insuffisante ou des appareils mal entretenus.</p>`,
    },
    {
      h2: "Les points de contrôle du diagnostic gaz",
      html: `<p>Notre diagnostiqueur vérifie quatre éléments clés selon la norme NF P 45-500 :</p>
<ul>
  <li><strong>Tuyauteries fixes</strong> : état, étanchéité, matériaux</li>
  <li><strong>Raccordements aux appareils</strong> : flexibles, accords, robinets de sécurité</li>
  <li><strong>Ventilation du logement</strong> : aérations hautes et basses, VMC</li>
  <li><strong>Combustion des appareils</strong> : chaudière, chauffe-eau, cuisinière</li>
</ul>`,
    },
    {
      h2: "Anomalies et mise en sécurité",
      html: `<p>En cas d'anomalie de type <strong>DGI (Danger Grave et Immédiat)</strong>, le diagnostiqueur a l'obligation de couper l'alimentation en gaz et d'alerter le distributeur. Ces situations critiques doivent être résolues avant toute remise en service.</p>`,
    },
  ],

  termites: [
    {
      h2: "Les termites, un ennemi invisible",
      html: `<p>Les termites sont des insectes xylophages qui se nourrissent du bois et peuvent causer des dégâts structurels majeurs : affaiblissement des charpentes, planchers qui s'effondrent, menuiseries creuses. Le danger est qu'ils agissent <strong>de manière invisible</strong>, à l'abri de la lumière, parfois pendant des années avant que les dégâts n'apparaissent.</p>`,
    },
    {
      h2: "Zones concernées en Île-de-France",
      html: `<p>Certaines communes de la petite et grande couronne parisienne sont classées en zone contaminée par arrêté préfectoral. Notre équipe vérifie gratuitement pour vous si votre bien est concerné avant l'intervention. Les zones évoluent régulièrement, nous nous tenons informés des dernières déclarations.</p>`,
    },
    {
      h2: "Déroulement du diagnostic",
      html: `<p>Le diagnostic est un <strong>repérage visuel</strong> des parties accessibles du bien, avec sondage non destructif du bois si nécessaire (frappe, sondage à la pointe). Sont contrôlés : charpente, planchers, solives, boiseries, escaliers, encadrements de portes et fenêtres. Le rapport indique la présence ou l'absence d'indices et sa <strong>validité est de 6 mois</strong>.</p>`,
    },
  ],

  "loi-carrez": [
    {
      h2: "Qu'est-ce que la Loi Carrez exactement ?",
      html: `<p>Votée en 1996, la <strong>Loi Carrez</strong> oblige le vendeur d'un lot de copropriété à mentionner sa superficie privative dans tous les documents de vente. Elle protège l'acquéreur contre les erreurs et lui donne un recours en cas d'écart important entre la superficie annoncée et la superficie réelle.</p>
<p>Attention, la Loi Carrez ne s'applique pas aux maisons individuelles, aux biens de moins de 8 m², ni aux caves, garages, emplacements de stationnement.</p>`,
    },
    {
      h2: "Comment se calcule la superficie Carrez ?",
      html: `<p>La superficie Carrez correspond à la superficie des planchers des locaux clos et couverts, déduction faite :</p>
<ul>
  <li>Des murs, cloisons, marches et cages d'escalier</li>
  <li>Des gaines et embrasures de portes et fenêtres</li>
  <li>Des surfaces dont la hauteur sous plafond est &lt; 1,80 m</li>
  <li>Des balcons, terrasses, jardins</li>
</ul>`,
    },
    {
      h2: "Erreur de mesurage : quels recours ?",
      html: `<p>Si l'écart entre la surface réelle et celle mentionnée dépasse <strong>5 %</strong>, l'acquéreur peut intenter une <strong>action en diminution du prix</strong> proportionnelle, dans un délai d'un an après l'acte authentique. C'est pourquoi il est essentiel de faire appel à un professionnel équipé de matériel de mesure certifié (télémètre laser).</p>`,
    },
  ],
};
